import styled from 'styled-components';

import { Text } from '@/nova/components';
import { CUST, CUST_GRADIENTS } from '../../../../theme/customizer-tokens';

type Tone = 'done' | 'current' | 'upcoming';
const descColor = (tone: Tone): string =>
  tone === 'done' ? CUST.doneChipText : tone === 'current' ? CUST.accentText : CUST.textMuted;

export const Panel = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-bottom: 24px;
  border: 1px solid ${CUST.panelBorder};
  border-radius: 16px;
  background-image: ${CUST_GRADIENTS.panel};
  overflow: hidden;
`;

export const Head = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px 24px 0;
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${CUST.white};
`;

export const TitleText = styled(Text)`
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  color: ${CUST.white};
  white-space: nowrap;
`;

export const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
`;

export const Seg = styled.div<{ $on: boolean }>`
  flex: 1 1 0;
  min-width: 1px;
  height: 4px;
  border-radius: 50px;
  background: ${({ $on }) => ($on ? CUST.progressFill : CUST.progressTrack)};
`;

export const Steps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 24px;

  &:not(:last-child) {
    padding-bottom: 24px;
    border-bottom: 1px solid ${CUST.panelDivider};
  }
`;

export const RowHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

export const StepLabel = styled(Text)`
  font-size: 12px;
  line-height: 16px;
  color: ${CUST.textMuted};
  white-space: nowrap;
`;

export const Chip = styled.div<{ $tone: 'current' | 'done' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  padding: 0 8px;
  border-radius: 12px;
  background: ${({ $tone }) => ($tone === 'done' ? CUST.doneChipBg : CUST.accentTint)};
`;

export const ChipText = styled(Text)<{ $tone: 'current' | 'done' }>`
  font-size: 12px;
  line-height: 16px;
  color: ${({ $tone }) => ($tone === 'done' ? CUST.doneChipText : CUST.accentText)};
  white-space: nowrap;
`;

export const Desc = styled(Text)<{ $tone: Tone }>`
  font-size: 20px;
  line-height: 1.15;
  font-weight: ${({ $tone }) => ($tone === 'current' ? 500 : 300)};
  color: ${({ $tone }) => descColor($tone)};
`;

/** Selection summary (country under step 1; name & domain under step 2). */
export const Summary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const SummaryLabel = styled(Text)`
  font-size: 12px;
  line-height: 16px;
  color: ${CUST.textMuted};
`;

export const SummaryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const FlagImg = styled.img`
  flex: 0 0 auto;
  width: 22px;
  height: 16px;
  border-radius: 3px;
  object-fit: cover;
  background: ${CUST.flagRing};
`;

export const SummaryValue = styled(Text)<{ $muted?: boolean }>`
  font-size: 15px;
  line-height: 1.3;
  color: ${({ $muted }) => ($muted ? CUST.textMuted : CUST.white)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** Step-3 Lumio hint ("Just a heads up!") — a cyan→magenta gradient-border card. */
export const HintCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px;
  border: 1px solid transparent;
  border-radius: 14px;
  background:
    linear-gradient(${CUST.menuBg}, ${CUST.menuBg}) padding-box,
    ${CUST_GRADIENTS.lumioText} border-box;
`;

export const HintTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${CUST.lumioGlyph};
`;

/** Multicolour Lumio mark beside "Just a heads up!" (same gradient asset as the
 *  "Lumio's suggestions" header). A gradient image, so a plain <img>. */
export const HintLumioMark = styled.img`
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  display: block;
`;

export const HintTitle = styled(Text)`
  font-size: 14px;
  font-weight: 600;
  background-image: ${CUST_GRADIENTS.lumioText};
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
`;

export const HintBody = styled(Text)`
  font-size: 13px;
  line-height: 1.4;
  color: ${CUST.textMuted};
`;